import React, { useEffect, useRef, useState } from "react";
import {
  TrendingUp,
  Clock,
  DollarSign,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ServiceCard = ({ icon: Icon, title, description, index, isVisible }) => {
  return (
    <div
      style={{
        flex: "0 0 auto",
        padding: "20px 10px",
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        textAlign: "center",
        transition: "all 0.5s ease",
        margin: "0 8px",
        width: "280px",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(50px)",
        animation: isVisible
          ? `fadeIn 0.5s ease forwards ${index * 0.2}s`
          : "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-10px)";
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(74, 35, 90, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.08)";
      }}
      className="service-card"
    >
      <div
        style={{
          backgroundColor: "#410684",
          width: "80px",
          height: "80px",
          borderRadius: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 0.8rem",
          background: "linear-gradient(135deg, #410684 0%, #663399 100%)",
        }}
        className="icon-container"
      >
        <Icon
          size={35}
          color="#ffffff"
          strokeWidth={1.5}
          className="service-icon"
        />
      </div>
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "700",
          lineHeight: "32px",
          marginBottom: "0.8rem",
          color: "#414042",
          fontFamily: "Oswald, sans-serif",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
        className="service-title"
      >
        {title}
      </h3>
      <p
        style={{
          color: "#414042",
          lineHeight: "20px",
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          maxWidth: "90%",
          margin: "0 auto",
        }}
        className="service-description"
      >
        {description}
      </p>
    </div>
  );
};

const MobileScrollIndicator = ({ isVisible }) => (
  <div
    style={{
      position: "absolute",
      bottom: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      padding: "10px 20px",
      borderRadius: "30px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      display: "flex",
      alignItems: "center",
      opacity: isVisible ? 1 : 0,
      transition: "opacity 0.3s ease",
      zIndex: 10,
    }}
    className="mobile-indicator"
  >
    <ChevronLeft size={20} />
    <span style={{ margin: "0 10px", whiteSpace: "nowrap" }} className="swipe-text">
      Swipe to explore
    </span>
    <ChevronRight size={20} />
  </div>
);

const ServicesGrid = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showMobileIndicator, setShowMobileIndicator] = useState(true);
  const containerRef = useRef(null);
  const scrollTimeout = useRef(null);

  const services = [
    {
      icon: TrendingUp,
      title: "Root Cause Analysis",
      description: "We reduce incidents by 35% to improve system stability.",
    },
    {
      icon: Clock,
      title: "Zero Downtime",
      description: "Our 24/7 proactive support ensures systems stay operational.",
    },
    {
      icon: DollarSign,
      title: "Optimize Cost",
      description: "We cut infrastructure expenses to reduce YoY investment.",
    },
    {
      icon: BookOpen,
      title: "Knowledge Base",
      description: "We leverage decades of expertise to enhance operations.",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    setTimeout(() => {
      setShowMobileIndicator(false);
    }, 5000);

    return () => observer.disconnect();
  }, []);

  const handleScroll = () => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    setShowMobileIndicator(false);

    scrollTimeout.current = setTimeout(() => {
      setShowMobileIndicator(true);
      setTimeout(() => {
        setShowMobileIndicator(false);
      }, 2000);
    }, 150);
  };

  return (
    <div
      style={{
        padding: "1.8rem 0",
        marginTop: "35px",
        background: "linear-gradient(135deg, #410684 0%, #663399 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        position: "relative",
      }}
      ref={containerRef}
      className="services-grid-container"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          width: "100%",
          overflowX: "auto",
          padding: "20px 0",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
        onScroll={handleScroll}
        onTouchStart={() => setShowMobileIndicator(false)}
        className="services-container"
      >
        <div className="services-wrapper">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>

      <MobileScrollIndicator isVisible={showMobileIndicator} />

      <style>
        {`
          .services-container::-webkit-scrollbar {
            display: none;
          }
          
          .services-wrapper {
            display: flex;
            padding: 0 16px;
            gap: 16px;
            min-width: min-content;
          }

          @media (max-width: 768px) {
            .services-wrapper {
              padding: 0 8px;
              gap: 12px;
            }
          }

          .service-card {
            min-width: 280px;
            max-width: 350px;
          }

          @media (max-width: 768px) {
            .service-card {
              min-width: 250px;
            }
          }

          @media (max-width: 480px) {
            .service-card {
              min-width: 220px;
            }

            .icon-container {
              width: 60px !important;
              height: 60px !important;
            }

            .service-icon {
              width: 25px;
              height: 25px;
            }

            .service-title {
              font-size: 16px !important;
              line-height: 24px !important;
            }

            .service-description {
              font-size: 12px !important;
              line-height: 18px !important;
            }

            .mobile-indicator {
              padding: 8px 15px !important;
            }

            .swipe-text {
              font-size: 12px;
            }

            .services-grid-container {
              padding: 1rem 0 !important;
              margin-top: 20px !important;
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(50px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default ServicesGrid;